import React, { useState, useEffect } from 'react';
import { Departamento, Usuario } from '../../types';
import api from '../../services/api';

interface FiltrosDepartamentoProps {
  departamentos: Departamento[];
  usuarios: Usuario[];
  onFiltroChange: (filtros: FiltrosState) => void;
  ocultarFiltroDepto?: boolean; // Líder não pode trocar de departamento
}

export interface FiltrosState {
  departamentoId: string;
  usuarioId: string;
  status: string;
  prioridade: string;
  dataInicio: string;
  dataFim: string;
}

const FiltrosDepartamento: React.FC<FiltrosDepartamentoProps> = ({
  departamentos,
  usuarios,
  onFiltroChange,
  ocultarFiltroDepto = false
}) => {
  const [filtros, setFiltros] = useState<FiltrosState>({
    departamentoId: '',
    usuarioId: '',
    status: '',
    prioridade: '',
    dataInicio: '',
    dataFim: '',
  });

  const [usuariosFiltrados, setUsuariosFiltrados] = useState<Usuario[]>([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtrosTemporarios, setFiltrosTemporarios] = useState<FiltrosState>(filtros);

  // Atualizar usuários filtrados quando o departamento mudar
  // Inclui membros (departamento_id) + líderes (departamento_lideres) do departamento
  useEffect(() => {
    const carregarUsuariosDoDepartamento = async () => {
      if (!filtrosTemporarios.departamentoId) {
        setUsuariosFiltrados(usuarios);
        return;
      }

      // Membros do departamento (departamento_id bate)
      const membros = usuarios.filter(
        u => u.departamentoId === filtrosTemporarios.departamentoId || u.departamento_id === filtrosTemporarios.departamentoId
      );

      // Buscar líderes do departamento via API
      try {
        const res = await api.get(`/departamentos/${filtrosTemporarios.departamentoId}`);
        const lideres: Usuario[] = res.data?.departamento?.lideres || [];

        // Mesclar membros + líderes sem duplicatas
        const idsJaIncluidos = new Set(membros.map(u => u.id));
        const lideresNovos = lideres.filter(l => !idsJaIncluidos.has(l.id)).map(l => ({
          ...l,
          cargo: l.cargo ? `${l.cargo} (Líder)` : 'Líder',
        }));

        setUsuariosFiltrados([...membros, ...lideresNovos]);
      } catch {
        setUsuariosFiltrados(membros);
      }
    };

    carregarUsuariosDoDepartamento();
  }, [filtrosTemporarios.departamentoId, usuarios]);

  const handleFiltroChange = (campo: keyof FiltrosState, valor: string) => {
    const novosFiltros = { ...filtrosTemporarios, [campo]: valor };
    
    // Se mudou o departamento, limpar o filtro de usuário
    if (campo === 'departamentoId') {
      novosFiltros.usuarioId = '';
    }
    
    console.log('[FiltrosDepartamento] Filtro alterado:', {
      campo,
      valor,
      novosFiltros
    });
    
    setFiltrosTemporarios(novosFiltros);
  };

  const aplicarFiltros = () => {
    setFiltros(filtrosTemporarios);
    onFiltroChange(filtrosTemporarios);
  };

  const limparFiltros = () => {
    const filtrosLimpos: FiltrosState = {
      departamentoId: '',
      usuarioId: '',
      status: '',
      prioridade: '',
      dataInicio: '',
      dataFim: '',
    };
    setFiltros(filtrosLimpos);
    setFiltrosTemporarios(filtrosLimpos);
    onFiltroChange(filtrosLimpos);
  };

  const contarFiltrosAtivos = () => {
    return Object.values(filtros).filter(v => v !== '').length;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-800">Filtros Avançados</h3>
          {contarFiltrosAtivos() > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
              {contarFiltrosAtivos()} ativo{contarFiltrosAtivos() > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {contarFiltrosAtivos() > 0 && (
            <button
              onClick={limparFiltros}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Limpar filtros
            </button>
          )}
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className="text-blue-600 hover:text-blue-800"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-5 w-5 transition-transform ${mostrarFiltros ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {mostrarFiltros && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Filtro por Departamento — oculto para Líder (fixado no seu dept) */}
          {!ocultarFiltroDepto && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Departamento
              </label>
              <select
                value={filtrosTemporarios.departamentoId}
                onChange={(e) => handleFiltroChange('departamentoId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os departamentos</option>
                {departamentos.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Filtro por Usuário */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuário
            </label>
            <select
              value={filtrosTemporarios.usuarioId}
              onChange={(e) => handleFiltroChange('usuarioId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os usuários</option>
              {usuariosFiltrados.map(user => (
                <option key={user.id} value={user.id}>
                  {user.nome} {user.cargo ? `- ${user.cargo}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filtrosTemporarios.status}
              onChange={(e) => handleFiltroChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os status</option>
              <option value="Pendente">Pendente</option>
              <option value="A iniciar">A iniciar</option>
              <option value="Em Andamento">Em Andamento</option>
              <option value="Concluída">Concluída</option>
              <option value="concluida_com_atraso">Concluídas com Atraso</option>
              <option value="Arquivada">Arquivada</option>
            </select>
          </div>

          {/* Filtro por Prioridade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prioridade
            </label>
            <select
              value={filtrosTemporarios.prioridade}
              onChange={(e) => handleFiltroChange('prioridade', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas as prioridades</option>
              <option value="Alta">Alta</option>
              <option value="Média">Média</option>
              <option value="Baixa">Baixa</option>
            </select>
          </div>

          {/* Filtro por Data Início */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Início
            </label>
            <input
              type="date"
              value={filtrosTemporarios.dataInicio}
              onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filtro por Data Fim */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Fim
            </label>
            <input
              type="date"
              value={filtrosTemporarios.dataFim}
              onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {mostrarFiltros && (
        <div className="flex justify-end mt-4 space-x-3">
          <button
            onClick={limparFiltros}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
          >
            Limpar
          </button>
          <button
            onClick={aplicarFiltros}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Buscar
          </button>
        </div>
      )}
    </div>
  );
};

export default FiltrosDepartamento;
